<script setup>
import { computed } from 'vue';
import { cva } from 'class-variance-authority';
import UiBadge from '@/components/ui/UiBadge.vue';

const props = defineProps({
  as: { type: String, default: 'h2' },
  eyebrow: { type: String, default: null },
  eyebrowIcon: { type: String, default: null },
  title: { type: String, required: true },
  subtitle: { type: String, default: null },
  align: { type: String, default: 'center' },
  tone: { type: String, default: 'light' },
  size: { type: String, default: 'lg' },
});

const wrapper = cva('flex flex-col gap-4', {
  variants: {
    align: { center: 'items-center text-center mx-auto max-w-2xl', start: 'items-start text-left' },
  },
});

const heading = cva('font-display font-semibold tracking-tight', {
  variants: {
    tone: { light: 'text-deen-ink', dark: 'text-white' },
    size: { md: 'text-3xl lg:text-4xl', lg: 'text-4xl lg:text-5xl' },
  },
});

const sub = cva('font-medium leading-relaxed', {
  variants: { tone: { light: 'text-deen-ink/60', dark: 'text-white/60' } },
});

const classes = computed(() => ({
  wrapper: wrapper({ align: props.align }),
  heading: heading({ tone: props.tone, size: props.size }),
  sub: sub({ tone: props.tone }),
}));
</script>

<template>
  <div :class="classes.wrapper">
    <UiBadge v-if="eyebrow" :tone="tone === 'dark' ? 'inverse' : 'ink'" :icon="eyebrowIcon">{{ eyebrow }}</UiBadge>
    <component :is="as" :class="classes.heading">{{ title }}</component>
    <p v-if="subtitle" :class="classes.sub">{{ subtitle }}</p>
    <slot />
  </div>
</template>
